import React, { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkspace } from '../WorkspaceContext'
import useCloseOnClickAway from '../../../shared/hooks/UseCloseOnClickAway'
import api from '../../../api'
import { downloadFile, downloadSnippet, downloadMultiple } from '../sidebar/DownloadHelper'
import { normalizeMime } from '../../../shared/utils/normalizeMime'
import { getSnippetExtension } from '../../../shared/utils/getFileIconAndExtensionHelper'
import { findFolderInTree, flattenFolderTreeWithDepth } from '../../../shared/utils/folderTree'
import { collectFolderSubtreeForSelection } from '../sidebar/Tabs/FilesTabHelpers'
import { canEditResource } from '../../../shared/utils/permissions'
import { canDropIntoFolder, moveResourceTo } from '../../../shared/utils/driveDnd'
import Download from '../../../shared/components/svgIcons/Download'
import TrashIcon from '../../../shared/components/svgIcons/TrashIcon'
import RenameIcon from '../../../shared/components/svgIcons/RenameIcon'
import OpenFileIcon from '../../../shared/components/svgIcons/OpenFileIcon'
import '../sidebar/sidebar.scss'

type Props = {
    rType: "file" | "snippet" | "folder";
    rId: string;
    creatorId: string;
    anchorRect: { top: number; left: number; bottom: number; right: number };
    onClose: () => void;
    onOpen: () => void;
    onRename: () => void;
}

const FileCardOptionsPopup = ({ rType, rId, creatorId, anchorRect, onClose, onOpen, onRename }: Props) => {
    const { roomId } = useParams();
    const { Snippets, Files, Folders, membersList, memberDetails, roomDetails, setLimitMessage, setLimitPopup, setDownloadFileErrorPopup, ctheme } = useWorkspace();
    const clientId = localStorage.getItem('flashroom_id');
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);

    useCloseOnClickAway({ targetRef: popupRef, onClose });

    const canEdit = canEditResource({
        creatorId,
        clientId,
        memberRole: memberDetails?.role,
        roomCreatorClientId: roomDetails?.creatorClientId,
        membersList,
    });

    const dragParams = useMemo(() => ({
        clientId,
        memberRole: memberDetails?.role,
        memberCanCreate: memberDetails?.canCreate,
        roomCreatorClientId: roomDetails?.creatorClientId,
        membersList,
    }), [clientId, memberDetails, roomDetails, membersList]);

    // Where the item lives right now, so the move picker can leave out
    // wherever it already is (its current parent folder, or the room root).
    const currentParentId = useMemo(() => {
        if (rType === 'folder') return findFolderInTree(Folders, rId)?.folderParent ?? null;
        if (rType === 'snippet') return Snippets.find(s => s._id === rId)?.folderParent ?? null;
        return Files.find(f => f._id === rId)?.folderParent ?? null;
    }, [rType, rId, Folders, Snippets, Files]);

    // Computed unconditionally (folder counts are capped at FOLDER_LIMIT=20,
    // cheap) so canMove below reflects real valid destinations, not just
    // "some folder exists somewhere in the room".
    const moveTargets = useMemo(() => {
        const excludeId = rType === 'folder' ? rId : null;
        return flattenFolderTreeWithDepth(Folders, excludeId)
            .filter(({ folder }) => folder._id !== currentParentId)
            .filter(({ folder }) =>
                canDropIntoFolder({ creatorId: folder.creatorId, folderPermission: folder.folderPermission }, creatorId, dragParams)
            );
    }, [Folders, rType, rId, creatorId, dragParams, currentParentId]);

    const canMove = canEdit && (currentParentId !== null || moveTargets.length > 0);

    const showError = (message: string) => {
        setLimitMessage(message);
        setLimitPopup(true);
    };

    const downloadClick = async () => {
        if (!roomId) return;
        try {
            if (rType === 'snippet') {
                const snip = Snippets.find(s => s._id === rId);
                if (!snip) return;
                let mime = '';
                let ext = '';
                if (snip.snippetType === 'text') {
                    mime = "text/plain;charset=utf-8";
                    ext = 'txt';
                } else {
                    const lang = snip.snippetLanguage || 'text';
                    mime = normalizeMime(lang);
                    ext = getSnippetExtension(lang);
                }
                await downloadSnippet(`${snip.snippetName}.${ext}`, snip.snipText, mime);
            } else if (rType === 'file') {
                const file = Files.find(f => f._id === rId);
                if (!file) return;
                await downloadFile(roomId, rId, file.name);
            } else {
                const folderObj = findFolderInTree(Folders, rId);
                if (!folderObj) return;
                const subtree = collectFolderSubtreeForSelection(folderObj);
                const fileIds = subtree.filter(i => i.resType === 'file').map(i => i.id);
                const snippetIds = subtree.filter(i => i.resType === 'snippet').map(i => i.id);
                const folderIds = subtree.filter(i => i.resType === 'folder').map(i => i.id);
                await downloadMultiple(roomId, snippetIds, fileIds, folderIds, {
                    name: folderObj.fname,
                    itemType: "folder",
                });
            }
        } catch {
            setDownloadFileErrorPopup(true);
        }
        onClose();
    };

    const deleteClick = async () => {
        if (!roomId) return;
        try {
            if (rType === 'snippet') {
                await api.delete(`/snippets/${rId}/room/${roomId}`);
            } else if (rType === 'file') {
                await api.delete(`/rooms/${roomId}/${rId}/files`);
            } else {
                await api.delete(`/rooms/${roomId}/folders/${rId}/delete`);
            }
            onClose();
        } catch {
            showError('Could not delete item. Please try again.');
        }
    };

    const changeFolderPermission = async (folderPermission: "restricted" | "open") => {
        if (!roomId) return;
        try {
            await api.patch(`/rooms/${roomId}/folders/${rId}/permission`, { folderPermission });
            onClose();
        } catch {
            showError('Could not update folder permissions. Please try again.');
        }
    };

    const changeSnippetPermission = async (perm: 'view' | 'edit') => {
        if (!roomId) return;
        const snip = Snippets.find(s => s._id === rId);
        if (!snip) return;
        try {
            await api.patch(`/snippets/updatesnippet/${roomId}/${rId}`, {
                snippetName: snip.snippetName,
                snipPermissions: perm,
            });
            onClose();
        } catch {
            showError('Could not update snippet permissions. Please try again.');
        }
    };

    const moveTo = async (targetFolderId: string | null) => {
        if (!roomId) return;
        try {
            await moveResourceTo(roomId, { kind: rType, id: rId }, targetFolderId);
            onClose();
        } catch {
            showError('Could not move item. Please try again.');
        }
    };

    const folder = rType === 'folder' ? findFolderInTree(Folders, rId) : null;
    const snippet = rType === 'snippet' ? Snippets.find(s => s._id === rId) : null;

    const left = Math.max(8, Math.min(anchorRect.left - 150, window.innerWidth - 250));
    const top = Math.min(anchorRect.bottom + 6, window.innerHeight - 260);

    return (
        <div
            ref={popupRef}
            className={`right-click-popup fade-in ${ctheme === 'light' ? 'light' : 'dark'}`}
            style={{ position: 'fixed', left, top, zIndex: 9999 }}
        >
            {confirmDelete ? (
                <div className='sb-rc-delete-popup'>
                    <h3>Are you sure you would like to delete this item?</h3>
                    <p>This action cannot be undone</p>
                    <div className='delete-pop-btns'>
                        <div onClick={deleteClick} className='delete-pop-btn dp-y-s'>Yes</div>
                        <div onClick={() => setConfirmDelete(false)} className='delete-pop-btn dp-c-s'>Cancel</div>
                    </div>
                </div>
            ) : moveOpen ? (
                <div className="rc-section">
                    <button onClick={() => setMoveOpen(false)} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                        <div className="rc-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </div>
                        <span className="rc-label">Back</span>
                    </button>

                    <div className="rc-move-list">
                        {currentParentId !== null && (
                            <button
                                type="button"
                                className="rc-move-item"
                                onClick={() => void moveTo(null)}
                            >
                                <img
                                    draggable={false}
                                    className="rc-move-icon"
                                    src={`${process.env.PUBLIC_URL}/images/${ctheme === 'light' ? 'room-icon' : 'room-icon-white'}.png`}
                                    alt=""
                                />
                                <span>{roomDetails?.roomName ?? 'My Drive'}</span>
                            </button>
                        )}

                        {moveTargets.map(({ folder: target, depth }) => (
                            <button
                                key={target._id}
                                type="button"
                                className="rc-move-item"
                                style={{ paddingLeft: 10 + depth * 16 }}
                                onClick={() => void moveTo(target._id)}
                            >
                                <img
                                    draggable={false}
                                    className="rc-move-icon"
                                    src={`${process.env.PUBLIC_URL}/images/folder-closed-dark.png`}
                                    alt=""
                                />
                                <span>{target.fname}</span>
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <>
                    <div className="rc-section">
                        <button onClick={onOpen} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                            <div className='rc-icon'>
                                <OpenFileIcon />
                            </div>
                            <span className="rc-label">Open</span>
                        </button>

                        <button onClick={downloadClick} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                            <div className="rc-icon">
                                <Download />
                            </div>
                            <span className="rc-label">Download</span>
                        </button>

                        {canEdit && (rType === 'folder' || rType === 'snippet') && (
                            <button onClick={() => { onRename(); onClose(); }} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                <div className="rc-icon">
                                    <RenameIcon />
                                </div>
                                <span className="rc-label">Rename</span>
                            </button>
                        )}

                        {canMove && (
                            <button onClick={() => setMoveOpen(true)} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                <div className="rc-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </div>
                                <span className="rc-label">Move</span>
                            </button>
                        )}

                        {canEdit && (
                            <button onClick={() => setConfirmDelete(true)} type="button" className="clickable rc-popup-btn quick-flex rc-btn danger">
                                <div className="rc-icon">
                                    <TrashIcon />
                                </div>
                                <span className="rc-label">Delete</span>
                            </button>
                        )}
                    </div>

                    {rType === 'folder' && folder && canEdit && (
                        <div className="rc-folder-permission">
                            <div className="rc-folder-permission-label">Folder Access</div>
                            <div className="rc-folder-permission-options">
                                {(["restricted", "open"] as const).map((option) => {
                                    const active = (folder.folderPermission ?? "restricted") === option;
                                    return (
                                        <button
                                            key={option}
                                            type="button"
                                            className={`rc-folder-permission-option ${active ? "active" : ""}`}
                                            onClick={() => { if (!active) void changeFolderPermission(option); }}
                                        >
                                            {option === "restricted" ? "Restricted" : "Open"}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {rType === 'snippet' && snippet && canEdit && (
                        <div className="rc-folder-permission">
                            <div className="rc-folder-permission-label">Snippet Access</div>
                            <div className="rc-folder-permission-options">
                                {(["view", "edit"] as const).map((option) => {
                                    const active = snippet.snipPermissions === option;
                                    return (
                                        <button
                                            key={option}
                                            type="button"
                                            className={`rc-folder-permission-option ${active ? "active" : ""}`}
                                            onClick={() => { if (!active) void changeSnippetPermission(option); }}
                                        >
                                            {option === "view" ? "Read Only" : "Open"}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default FileCardOptionsPopup
